"use client";

import React, { useState, useMemo } from "react";
import { NavigationProvider } from "./NavigationContext";
import { useLocale } from "next-intl";
import { usePathname } from "i18n/routing";
import darkTheme from "themes/dark";
import OverlayMenu from "../overlay";
import { NavContext } from "./nav-context";
import { Grid } from "components/layout/Grid";
import { NavigationWrapper } from "./style";
import { FILES } from "i18n/constants";
import { useTranslations } from "i18n";
import type { AppTheme } from "themes/global";
import type { NavLinkKey } from "./constants";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";
import { getMainNavigationLinks } from "./navigation-links";
import NavigationContainer from "./components/NavigationContainer";
import SubMenuContainer from "./components/SubMenuContainer";
import GeminiInstitutionsBanner from "../banners/gemini-institutions-banner";
import { isTouchDevice } from "./components/MenuItemWithDropdown";
import { usePathnameChecks } from "hooks/usePathnameChecks";
import { useCreditCardSubmenuTest } from "./credit-card-ab-test";

interface Props {
  hideNavigationLinks?: boolean;
  theme?: AppTheme;
  rightItems?: React.ReactNode;
}

// Base links that don't have overlays by default
const BASE_NO_OVERLAY: NavLinkKey[] = [
  "Prices",
  "Staking",
  "CreditCard",
  "Perpetuals",
  "TokenizedStocks"
];

const MainNav = ({
  theme = darkTheme,
  hideNavigationLinks = false,
  rightItems
}: Props) => {
  const pathname = usePathname();
  const locale = useLocale();
  const { country } = useCountryStateCookies();
  const t = useTranslations(FILES.COMMON);

  const [activeMenu, setActiveMenu] = useState<NavLinkKey | null>(null);

  const { isGoogleSearchPath, isHighConversionPath, isPartnerPath } =
    usePathnameChecks();

  // Memoize base navigation links to ensure stable reference
  const baseNavigationLinks = useMemo(
    () =>
      getMainNavigationLinks({
        t,
        locale,
        countryCode: country
      }),
    [t, locale, country]
  );

  // Apply A/B test for credit card submenu and get modified navigation links
  const navigationLinks = useCreditCardSubmenuTest(
    baseNavigationLinks,
    country
  );

  // Get menu items that shouldn't have overlay, accounting for credit card A/B test
  const noOverlayItems = useMemo(() => {
    // Find if credit card has menu after the A/B test application
    const creditCardHasMenu = navigationLinks.some(
      (link) => link.key === "CreditCard" && link.hasMenu
    );

    // If credit card has menu, remove it from no-overlay items
    return creditCardHasMenu
      ? BASE_NO_OVERLAY.filter((item) => item !== "CreditCard")
      : BASE_NO_OVERLAY;
  }, [navigationLinks]);

  // Check if active menu should display overlay
  const hasOverlay =
    activeMenu !== null && !noOverlayItems.includes(activeMenu);

  // Create navigation provider value
  const navigationValue = {
    activeMenu,
    setActiveMenu,
    isCloseButtonActive: false,
    setIsCloseButtonActive: () => {}
  };

  return (
    <NavigationProvider value={navigationValue}>
      <NavigationWrapper
        theme={theme}
        noPadding
        isOpen={hasOverlay}
        onMouseLeave={() => {
          if (!isTouchDevice()) setActiveMenu(null);
        }}
      >
        <NavContext.Provider
          value={{
            handleCloseMenu: () => setActiveMenu(null)
          }}
        >
          <Grid smSpacing={0}>
            <OverlayMenu
              isOpen={hasOverlay}
              handleClose={() => setActiveMenu(null)}
            >
              <NavigationContainer
                theme={theme}
                hideNavigationLinks={hideNavigationLinks}
                hidePathNavigationLinks={
                  isGoogleSearchPath || isPartnerPath || isHighConversionPath
                }
                isGoogleSearchPage={isGoogleSearchPath}
                navigationLinks={navigationLinks}
                rightItems={rightItems}
              />
            </OverlayMenu>
          </Grid>

          {!hasOverlay && <GeminiInstitutionsBanner path={pathname} />}

          <SubMenuContainer hasOverlay={hasOverlay} />
        </NavContext.Provider>
      </NavigationWrapper>
    </NavigationProvider>
  );
};

export default MainNav;
